import { Box, Typography, Dialog, DialogContent, Checkbox, Button, IconButton } from "@mui/material";
import { FaArrowLeft } from "react-icons/fa";
import { MdEventSeat, MdQrCodeScanner } from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PassengerInfoCard } from "../components/passengerboarding/PassengerInfoCard";
import { QrScannerScreen } from "../components/passengerboarding/QrScannerScreen";
import { alertAtom, globalLoaderAtom } from "../jotaiState/globalStateAtoms";
import { useAtomValue, useSetAtom } from "jotai";
import { tripAtom } from "../components/JotaiStates/commonStateAtoms";
import { apiGet } from "../helper/utils";
import { GetBoardingReportData, ValidateBoarding, ValidateBoardingForAndroid } from "../helper/apiUrls";
import { parseQrBookingData } from "../helper/parseQrBookingData";
import { requestCameraAccess } from "../helper/requestCameraAccess";
import { parseBoardingContextFromUrlParams } from "../helper/passengerBoardingDeepLink";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import useLogin from "../components/common/useLogin";

dayjs.extend(customParseFormat);

const urlBoardingPageLoads = new Map();

const sortByPickupDateTime = (list) =>
  [...list].sort((a, b) => {
    const dateA = dayjs(a.pickupdatetime, "MM-DD-YYYY HH:mm:ss", true);
    const dateB = dayjs(b.pickupdatetime, "MM-DD-YYYY HH:mm:ss", true);
    if (!dateA.isValid() && !dateB.isValid()) return 0;
    if (!dateA.isValid()) return 1;
    if (!dateB.isValid()) return -1;
    return dateA.valueOf() - dateB.valueOf();
  });

export const PassengerBoarding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setLoader = useSetAtom(globalLoaderAtom);
  const trip = useAtomValue(tripAtom);
  const setAlert = useSetAtom(alertAtom);
  const { login } = useLogin();

  const urlQuery = searchParams.toString() || window.location.search.replace(/^\?/, "");
  const urlContext = useMemo(
    () => parseBoardingContextFromUrlParams(new URLSearchParams(urlQuery)),
    [urlQuery]
  );
  const isUrlBoarding = urlContext.isValid;

  const [passList, setPassList] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedPNR, setSelectedPNR] = useState(null);
  const [selectedPassengers, setSelectedPassengers] = useState([]);

  const getTripDetails = () => {
    setLoader(true);
    const payload = {
      intTripID: trip.TripID,
      dtJDate: dayjs(trip.JourneyDate).format("YYYY-MM-DD"),
    };

    apiGet(GetBoardingReportData, payload)
      .then((res) => {
        if (!res.IsSuccess) {
          throw new Error(res.ErrorMessage || "Error fetching trip details");
        }
        const data = JSON.parse(res.Data);
        setPassList(sortByPickupDateTime(data?.Table || []));
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message, nextId: "" });
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const getUrlBoardingList = () => {
    setLoader(true);
    const payload = {
      intTripID: urlContext.tripId,
      dtJDate: dayjs(urlContext.journeyDate).format("YYYY-MM-DD"),
    };

    apiGet(GetBoardingReportData, payload)
      .then((res) => {
        if (!res.IsSuccess) {
          throw new Error(res.ErrorMessage || "Error fetching trip details");
        }
        const data = JSON.parse(res.Data);
        setPassList(sortByPickupDateTime(data?.Table || []));
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message, nextId: "" });
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    const liveContext = parseBoardingContextFromUrlParams(
      new URLSearchParams(window.location.search)
    );
    if (liveContext.isValid) return;
    if ((new URLSearchParams(window.location.search).get("ptfrm") || "").toLowerCase() === "android") {
      return;
    }
    if (!login?.DriverID) {
      navigate("/login", { replace: true });
      return;
    }
    getTripDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isUrlBoarding) {
      return;
    }

    const key = `${urlContext.tripId}|${urlContext.journeyDate}`;
    if (!urlBoardingPageLoads.has(key)) {
      urlBoardingPageLoads.set(
        key,
        apiGet(GetBoardingReportData, {
          intTripID: urlContext.tripId,
          dtJDate: dayjs(urlContext.journeyDate).format("YYYY-MM-DD"),
        }).then((res) => {
          if (!res.IsSuccess) {
            throw new Error(res.ErrorMessage || "Error fetching trip details");
          }
          return sortByPickupDateTime(JSON.parse(res.Data)?.Table || []);
        }).catch((err) => {
          urlBoardingPageLoads.delete(key);
          throw err;
        })
      );
    }

    let cancelled = false;
    setLoader(true);
    urlBoardingPageLoads
      .get(key)
      .then((list) => {
        if (cancelled) return;
        setPassList(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setAlert({ show: true, msg: err.message, nextId: "" });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoader(false);
        }
      });

    return () => {
      cancelled = true;
      setLoader(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrlBoarding, urlContext.tripId, urlContext.journeyDate]);

  const boardedCount = passList?.filter((p) => p?.PaxStatus === "B")?.length ?? 0;
  const yetToBoardCount = passList?.filter((p) => p?.PaxStatus !== "B")?.length ?? 0;

  const openValidationPopup = useCallback((passenger, passengersToSelect) => {
    const pnrPassengers = passList.filter((p) => p.BookingID === passenger.BookingID);
    setSelectedPNR(passenger.BookingID);
    setSelectedPassengers(passengersToSelect ?? pnrPassengers);
    setOpenPopup(true);
  }, [passList]);

  // const handleCardClick = (passenger) => {
  //   openValidationPopup(passenger);
  // };

  const handleOpenScanner = async () => {
    try {
      await requestCameraAccess();
    } catch {
      // Still open scanner so user can retry or upload QR from gallery.
    }
    setShowScanner(true);
  };

  const findPassengersFromQr = useCallback(
    (decodedText) => {
      const qrData = parseQrBookingData(decodedText);
      if (!qrData?.bookingId) {
        return { error: "Invalid QR code" };
      }

      const bookingPassengers = passList.filter(
        (p) =>
          String(p.BookingID) === String(qrData.bookingId) ||
          String(p.TicketNo) === String(qrData.bookingId)
      );

      if (!bookingPassengers.length) {
        return { error: "No matching passenger found for this QR" };
      }

      const selected =
        qrData.seatNos.length > 0
          ? bookingPassengers.filter((p) => qrData.seatNos.includes(String(p.SeatNo)))
          : bookingPassengers;

      if (!selected.length) {
        return { error: "No matching seats found for this QR" };
      }

      return { passenger: bookingPassengers[0], selectedPassengers: selected };
    },
    [passList]
  );

  const handleQrScan = useCallback(
    (decodedText) => {
      setShowScanner(false);
      const result = findPassengersFromQr(decodedText);

      if (result.error) {
        setAlert({ show: true, msg: result.error, nextId: "" });
        return;
      }

      openValidationPopup(result.passenger, result.selectedPassengers);
    },
    [findPassengersFromQr, openValidationPopup, setAlert]
  );

  const samePNRPassengers = passList?.filter((p) => p.BookingID === selectedPNR) || [];
  const isAllSelected =
    samePNRPassengers.length > 0 &&
    samePNRPassengers.every((passenger) =>
      selectedPassengers.some((selected) => selected.SeatNo === passenger.SeatNo)
    );

  const togglePassengerSelection = (passenger) => {
    setSelectedPassengers((prev) => {
      const exists = prev.some((p) => p.SeatNo === passenger.SeatNo);
      if (exists) {
        return prev.filter((p) => p.SeatNo !== passenger.SeatNo);
      }
      return [...prev, passenger];
    });
  };

  const toggleSelectAll = () => {
    setSelectedPassengers(isAllSelected ? [] : samePNRPassengers);
  };

  const handleBoarded = () => {
    if (isUrlBoarding) {
      setLoader(true);
      apiGet(ValidateBoardingForAndroid, {
        intUserId: urlContext.userId,
        iCompanyid: urlContext.companyId,
        intBookingID: selectedPNR,
        strUpdateType: "BOR",
        strPaxStatus: "B",
        strboardingTimeStamp: dayjs(urlContext.journeyDate).format("DD-MMM-YYYY"),
      })
        .then((res) => {
          if (!res.IsSuccess) {
            throw new Error(res.ErrorMessage);
          }
          setAlert({ show: true, msg: "PNR Boarded Successfully!", nextId: "" });
          getUrlBoardingList();
        })
        .catch((err) => {
          setAlert({ show: true, msg: err.message, nextId: "" });
        })
        .finally(() => {
          setLoader(false);
        });
      setOpenPopup(false);
      return;
    }

    setLoader(true);
    const payload = {
      intUserId: login.DriverID,
      iCompanyid: trip.CompanyId,
      intBookingID: selectedPNR,
      strUpdateType: "BOR",
      strPaxStatus: "B",
      strboardingTimeStamp: dayjs(trip.JourneyDate).format("DD-MMM-YYYY"),
    };
    apiGet(ValidateBoarding, payload)
      .then((res) => {
        if (!res.IsSuccess) {
          throw new Error(res.ErrorMessage);
        }
        setAlert({ show: true, msg: "PNR Boarded Successfully!", nextId: "" });
        getTripDetails();
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message, nextId: "" });
      })
      .finally(() => {
        setLoader(false);
      });
    setOpenPopup(false);
  };

  return (
    <Box className="trip-details-container">
      <Box
        display="flex"
        className="trip-details-header"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <FaArrowLeft
            className="back-icon"
            onClick={() => (isUrlBoarding ? navigate("/login") : navigate(-1))}
          />
          <Typography variant="h6" fontWeight={600} color="white">
            Boarding Report
          </Typography>
        </Box>
        <IconButton onClick={handleOpenScanner} sx={{ color: "white", p: 0 }}>
          <MdQrCodeScanner size={24} />
        </IconButton>
      </Box>

      <Box bgcolor="table.headBackground" p={2}>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            Passenger Boarded
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {`${passList?.length} Pax`}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between">
          <Typography fontSize={12} fontWeight={600}>
            {`Boarded Pax - ${boardedCount} Pax`}
          </Typography>
          <Typography fontSize={12} fontWeight={600}>
            {`Yet To Be Boarded - ${yetToBoardCount} Pax`}
          </Typography>
        </Box>
      </Box>

      <Box padding={1} display="flex" flexDirection="column" gap={1}>
        {passList?.map((p) => (
          <div
            key={`${p.BookingID}-${p.SeatNo}`}
            // onClick={() => handleCardClick(p)}
          >
            <PassengerInfoCard passenger={p} onScanClick={handleOpenScanner} />
          </div>
        ))}
      </Box>

      <QrScannerScreen
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleQrScan}
      />

      <Dialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            mx: 2,
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bgcolor="#0086FF"
          color="white"
          px={2.5}
          py={2}
        >
          <Typography fontWeight={500} fontSize={16} color="white">
            QR Validation
          </Typography>
          <Typography fontWeight={500} fontSize={16} color="white">
            {`PNR- ${selectedPNR}`}
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns="96px 1fr 56px"
          alignItems="center"
          px={1.5}
          py={1.5}
          bgcolor="#E8F1FA"
          borderBottom="1px solid #D9D9D9"
        >
          <Typography fontWeight={600} fontSize={13}>
            Seat No.
          </Typography>
          <Typography fontWeight={600} fontSize={13}>
            Passenger Name
          </Typography>
          <Box display="flex" justifyContent="flex-end">
            <Checkbox
              checked={isAllSelected}
              indeterminate={selectedPassengers.length > 0 && !isAllSelected}
              onChange={toggleSelectAll}
              sx={{
                color: "#7A7A7A",
                "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                  color: "#1686F0",
                },
              }}
            />
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {samePNRPassengers.map((p) => (
            <Box
              key={`${p.BookingID}-${p.SeatNo}`}
              display="grid"
              gridTemplateColumns="96px 1fr 56px"
              alignItems="center"
              px={1.5}
              py={1.5}
              borderBottom="1px solid #E6E6E6"
              color="#1A1A1A"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontSize={14}>{p.SeatNo}</Typography>
                <MdEventSeat color="#1686F0" size={20} />
                <Typography fontSize={14}>{p.Gender}</Typography>
              </Box>

              <Typography fontSize={14}>{p.PaxName}</Typography>

              <Box display="flex" justifyContent="flex-end">
                <Checkbox
                  checked={selectedPassengers.some((sp) => sp.SeatNo === p.SeatNo)}
                  onChange={() => togglePassengerSelection(p)}
                  sx={{
                    color: "#8A8A8A",
                    "&.Mui-checked": {
                      color: "#1686F0",
                    },
                  }}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>

        <Button
          variant="contained"
          fullWidth
          onClick={handleBoarded}
          disabled={!selectedPassengers.length}
          sx={{
            borderRadius: 0,
            py: 1.5,
            bgcolor: "#FF6A00",
            fontWeight: 600,
            fontSize: 16,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#E85F00",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              bgcolor: "#FFB37A",
              color: "white",
            },
          }}
        >
          VALIDATE
        </Button>
      </Dialog>
    </Box>
  );
};
